import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'

/**
 * Generic legal page component that loads markdown from docs/ and renders it.
 * Single source of truth: /docs/{filename}.md
 *
 * Usage:
 *   <LegalPageMarkdown filename="PRIVACY-POLICY" title="Política de Privacidade" />
 *
 * The markdown file is loaded from /docs/{filename}.md (copied to public/docs/ at build time).
 */
interface LegalPageMarkdownProps {
  filename: string
  title: string
  otherLink?: { href: string; label: string }
}

export function LegalPageMarkdown({ filename, title, otherLink }: LegalPageMarkdownProps) {
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        const response = await fetch(`/docs/${filename}.md`)
        if (!response.ok) {
          throw new Error(`Failed to load ${filename}.md: ${response.statusText}`)
        }
        const text = await response.text()
        setContent(text)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load document')
        console.error(`Error loading ${filename}.md:`, err)
      } finally {
        setLoading(false)
      }
    }

    loadMarkdown()
  }, [filename])

  return (
    <div
      style={{
        background: 'var(--cream)',
        color: 'var(--ink)',
        minHeight: '100vh',
        padding: '4rem 1.5rem 6rem',
      }}
    >
      <article
        style={{
          maxWidth: '720px',
          margin: '0 auto',
          fontFamily: 'var(--sans)',
          fontSize: '16px',
          lineHeight: 1.7,
        }}
      >
        {/* Header */}
        <header style={{ marginBottom: '3rem' }}>
          <Link
            to="/"
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '14px',
              color: 'var(--ink-muted)',
              textDecoration: 'none',
              display: 'inline-block',
              marginBottom: '2rem',
            }}
          >
            ← seGuru
          </Link>
          <h1
            style={{
              fontFamily: 'var(--serif)',
              fontSize: 'clamp(2.2rem, 5vw, 3rem)',
              fontWeight: 400,
              lineHeight: 1.15,
              margin: '0 0 0.5rem',
            }}
          >
            {title}
          </h1>
        </header>

        {loading && <p style={{ color: 'var(--ink-muted)' }}>Carregando...</p>}

        {error && (
          <p style={{ color: '#d32f2f' }}>
            Erro ao carregar documento: {error}
          </p>
        )}

        {!loading && !error && (
          <div
            style={{
              /* Typography */
              '& h2': {
                fontFamily: 'var(--serif)',
                fontSize: '1.7rem',
                fontWeight: 400,
                lineHeight: 1.25,
                margin: '3rem 0 1rem',
              },
              '& h3': {
                fontFamily: 'var(--sans)',
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.3,
                margin: '2rem 0 0.5rem',
                color: 'var(--ink-light)',
              },
              '& p': {
                marginBottom: '1em',
              },
              '& ul, & ol': {
                marginBottom: '1em',
                paddingLeft: '1.5em',
              },
              '& li': {
                marginBottom: '0.5em',
              },
              '& a': {
                color: 'var(--accent)',
                textDecoration: 'underline',
              },
              '& code': {
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                fontSize: '0.85em',
                background: 'var(--cream-dark)',
                padding: '0.1em 0.4em',
                borderRadius: '4px',
              },
              '& pre': {
                background: 'var(--cream-dark)',
                padding: '1em',
                borderRadius: '4px',
                overflowX: 'auto',
              },
            } as any}
          >
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}

        {/* Footer */}
        <footer
          style={{
            marginTop: '4rem',
            paddingTop: '2rem',
            borderTop: '1px solid var(--border)',
            color: 'var(--ink-muted)',
            fontSize: '13px',
          }}
        >
          {otherLink && (
            <>
              <Link
                to={otherLink.href}
                style={{
                  color: 'var(--accent)',
                  textDecoration: 'underline',
                }}
              >
                {otherLink.label}
              </Link>
              {' · '}
            </>
          )}
          <Link
            to="/"
            style={{
              color: 'var(--accent)',
              textDecoration: 'underline',
            }}
          >
            Voltar à homepage
          </Link>
        </footer>
      </article>
    </div>
  )
}
